/**
 * Tier 2: perspective warp of a flat device screen.
 *
 * This is **not 3D**, and it is deliberately not WebGL. A device that leans back is a flat
 * image mapped through a homography, so it needs one textured quad — no lighting, no
 * shadows, no materials. That is why it costs a couple of milliseconds even on a CPU-only
 * server, while a genuine 3D device (Tier 3, `device.kind: "3d"`) costs ~650 ms under
 * software rendering.
 *
 * Staying on Canvas2D is also what keeps the renderer isomorphic: a WebGL approach would
 * work in the browser and simply not exist in Node, splitting the renderer in two — the
 * exact outcome the environment boundary exists to prevent.
 *
 * The warp is a grid of affine-mapped cells clipped to their own quad. Canvas2D has no
 * projective transform, so this is the standard way to fake one: with enough cells the
 * seams are invisible, and each cell's clip is grown slightly so antialiasing does not
 * leave hairline cracks between them.
 */
import type { Ctx2D, RenderEnvironment, RenderImage } from './env';
import type { Point, Quad, Rect } from './fit';

/** Heckbert's closed-form homography from the unit square to a quad. */
interface Homography {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
  g: number;
  h: number;
}

/**
 * Corners must be given as (0,0), (1,0), (1,1), (0,1) of the unit square.
 *
 * Degenerates to an affine map when the quad is a parallelogram, which is the common case
 * (a pure tilt with no perspective foreshortening).
 */
export function unitSquareHomography(quad: Quad): Homography {
  const [p0, p1, p2, p3] = quad.corners;

  const dx1 = p1.x - p2.x;
  const dx2 = p3.x - p2.x;
  const dx3 = p0.x - p1.x + p2.x - p3.x;
  const dy1 = p1.y - p2.y;
  const dy2 = p3.y - p2.y;
  const dy3 = p0.y - p1.y + p2.y - p3.y;

  if (dx3 === 0 && dy3 === 0) {
    return {
      a: p1.x - p0.x,
      b: p3.x - p0.x,
      c: p0.x,
      d: p1.y - p0.y,
      e: p3.y - p0.y,
      f: p0.y,
      g: 0,
      h: 0,
    };
  }

  const denom = dx1 * dy2 - dx2 * dy1;
  if (denom === 0) {
    throw new Error('Degenerate quad: the perspective corners are collinear.');
  }

  const g = (dx3 * dy2 - dx2 * dy3) / denom;
  const h = (dx1 * dy3 - dx3 * dy1) / denom;

  return {
    a: p1.x - p0.x + g * p1.x,
    b: p3.x - p0.x + h * p3.x,
    c: p0.x,
    d: p1.y - p0.y + g * p1.y,
    e: p3.y - p0.y + h * p3.y,
    f: p0.y,
    g,
    h,
  };
}

/** Map a unit-square point through a homography. */
export function applyHomography(m: Homography, u: number, v: number): Point {
  const denom = m.g * u + m.h * v + 1;
  return {
    x: (m.a * u + m.b * v + m.c) / denom,
    y: (m.d * u + m.e * v + m.f) / denom,
  };
}

export interface PerspectiveParams {
  /** Lean back: positive tips the top edge away from the viewer. Degrees. */
  tiltX: number;
  /** Turn: positive swings the right edge away from the viewer. Degrees. */
  tiltY: number;
  /** Focal length in canvas pixels; lower is a stronger, closer perspective. */
  perspective: number;
}

/**
 * Project an axis-aligned rect into a quad that reads as a device leaning in space.
 *
 * Sign convention, chosen to read the way a template author expects:
 *   - **positive `tiltX` tips the top edge away** (the device leans back). Verified by the
 *     test asserting the top edge is the narrower one.
 *   - **positive `tiltY` turns the right edge away** (the device turns to its left).
 *
 * Getting this backwards is not a crash, it is a device that leans the wrong way, which is
 * why the tests pin the direction rather than only the geometry.
 *
 * Corner order is (0,0), (1,0), (1,1), (0,1) so the result feeds `unitSquareHomography`
 * directly.
 */
export function projectedQuad(rect: Rect, params: PerspectiveParams): Quad {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const hw = rect.width / 2;
  const hh = rect.height / 2;

  const rx = (params.tiltX * Math.PI) / 180;
  const ry = (params.tiltY * Math.PI) / 180;
  const cosX = Math.cos(rx);
  const sinX = Math.sin(rx);
  const cosY = Math.cos(ry);
  const sinY = Math.sin(ry);
  const focal = Math.max(params.perspective, 1);

  const project = (x: number, y: number): Point => {
    // z grows away from the viewer.
    const y1 = y * cosX;
    const z1 = -y * sinX;
    const x2 = x * cosY - z1 * sinY;
    const z2 = x * sinY + z1 * cosY;
    const scale = focal / (focal + z2);
    return { x: cx + x2 * scale, y: cy + y1 * scale };
  };

  return {
    corners: [
      project(-hw, -hh),
      project(hw, -hh),
      project(hw, hh),
      project(-hw, hh),
    ],
  };
}

export interface WarpOptions {
  /** Grid resolution per axis. 16 is smooth for a phone-sized device; raise for a foldable. */
  steps?: number;
  /** Grown each way so antialiased cell edges do not leave seams. */
  seamPixels?: number;
}

/**
 * Draw `image`'s `source` region through `destQuad`.
 *
 * Returns the number of cells drawn, which the benchmark uses to explain its own numbers.
 */
export function warpImage(
  ctx: Ctx2D,
  env: RenderEnvironment,
  image: RenderImage,
  source: Rect,
  destQuad: Quad,
  options: WarpOptions = {},
): number {
  const steps = Math.max(1, options.steps ?? 16);
  const seam = options.seamPixels ?? 0.75;
  const m = unitSquareHomography(destQuad);

  for (let iy = 0; iy < steps; iy += 1) {
    for (let ix = 0; ix < steps; ix += 1) {
      const u0 = ix / steps;
      const u1 = (ix + 1) / steps;
      const v0 = iy / steps;
      const v1 = (iy + 1) / steps;

      const tl = applyHomography(m, u0, v0);
      const tr = applyHomography(m, u1, v0);
      const br = applyHomography(m, u1, v1);
      const bl = applyHomography(m, u0, v1);

      // Grow the clip outward from the cell centre so neighbouring cells overlap rather
      // than leaving a hairline of background between them.
      const centre = {
        x: (tl.x + tr.x + br.x + bl.x) / 4,
        y: (tl.y + tr.y + br.y + bl.y) / 4,
      };
      const grow = (p: Point): Point => {
        const dx = p.x - centre.x;
        const dy = p.y - centre.y;
        const len = Math.hypot(dx, dy) || 1;
        return { x: p.x + (dx / len) * seam, y: p.y + (dy / len) * seam };
      };
      const gtl = grow(tl);
      const gtr = grow(tr);
      const gbr = grow(br);
      const gbl = grow(bl);

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(gtl.x, gtl.y);
      ctx.lineTo(gtr.x, gtr.y);
      ctx.lineTo(gbr.x, gbr.y);
      ctx.lineTo(gbl.x, gbl.y);
      ctx.closePath();
      ctx.clip();

      // Affine map from the unit square to three of the four cell corners. The fourth is
      // approximate, which is precisely what the clip above hides.
      ctx.setTransform(tr.x - tl.x, tr.y - tl.y, bl.x - tl.x, bl.y - tl.y, tl.x, tl.y);
      env.drawImage(
        ctx,
        image,
        source.x + u0 * source.width,
        source.y + v0 * source.height,
        Math.max((u1 - u0) * source.width, 0.01),
        Math.max((v1 - v0) * source.height, 0.01),
        0,
        0,
        1,
        1,
      );
      ctx.restore();
    }
  }

  return steps * steps;
}
